"use client"

import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import { useTheme } from "next-themes"
import { useColorMode } from "@chakra-ui/react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useCloseMobileSidebar,
} from "@/components/ui/sidebar"
import {
  renderRouteIconWithAnimRef,
  sidebarNavIconShellClass,
  useSidebarNavIconAnimation,
} from "@/components/Sidebar/sidebarNavIconAnimation"
import { BellIcon } from "@/components/ui/bell-icon"
import { CreditCardIcon } from "@/components/ui/credit-card-icon"
import { LogoutIcon } from "@/components/ui/logout-icon"
import { MoonIcon } from "@/components/ui/moon"
import { SunIcon } from "@/components/ui/sun"
import { Switch } from "@/components/ui/switch"
import { UserIcon } from "@/components/ui/user-icon"
import { useClerkAuth } from "@/hooks/useClerkAuth"
import { useSidebarExpandedMenuChrome } from "@/hooks/useSidebarExpandedMenuChrome"

import type { ClerkUserData } from "@/types/clerk"
import {
  sidebarGray500Class,
  sidebarSecondaryTextClass,
  sidebarMenuButtonIconRailClass,
  notificationsCentreIconRailButtonClass,
  sidebarLogoutIconClass,
  sidebarProfileMenuIconClassName,
  sidebarProfileMenuItemClassName,
  sidebarTropicalSurfClass,
} from "@/constants/sidebarChrome"
import { cn } from "@/lib/utils"
import { ChevronsUpDown } from "lucide-react"

import avatar4 from "@/assets/img/avatars/avatar4.png"
import {
  ACCOUNT_SETTINGS_RETURN_PATH_KEY,
  accountSettingsTabPath,
} from "@/constants/accountSettingsSidebar"
import {
  formatNotificationsProfileMenuLabel,
  NOTIFICATIONS_CENTRE_RETURN_PATH_KEY,
  notificationsCentrePath,
} from "@/constants/notificationsCentre"
import { useUnreadNotificationCount } from "@/hooks/useUnreadNotificationCount"

function userDisplayName(user: ClerkUserData | null): string {
  if (!user) return "User"
  const n = `${user.firstName || ""} ${user.lastName || ""}`.trim()
  return n || "User"
}

function userEmail(user: ClerkUserData | null): string {
  return user?.primaryEmailAddress || user?.emailAddresses?.[0]?.emailAddress || "No email"
}

function userAvatarSrc(user: ClerkUserData | null): string {
  /** Clerk image updates when the user changes their photo in account settings. */
  return user?.imageUrl || avatar4
}

function userInitials(user: ClerkUserData | null): string {
  if (!user) return "?"
  const f = user.firstName?.[0]
  const l = user.lastName?.[0]
  const e = (user.primaryEmailAddress || user.emailAddresses?.[0]?.emailAddress)?.[0]
  return `${(f || e || "?").toUpperCase()}${(l || "").toUpperCase()}`.slice(0, 2)
}

const navUserMenuButtonClass = cn(
  "gap-2.5 !pl-2 !pr-2 !py-2",
  sidebarMenuButtonIconRailClass,
  "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
)

function navUserTriggerClassName(iconRailOnly: boolean) {
  return cn(navUserMenuButtonClass, iconRailOnly && notificationsCentreIconRailButtonClass)
}

function ProfileMenuItemLink({
  to,
  onNavigate,
  label,
  icon,
}: {
  to: string
  onNavigate: () => void
  label: string
  icon: React.ReactNode
}) {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const { iconAnimRef, linkHandlers } = useSidebarNavIconAnimation()

  return (
    <DropdownMenuItem asChild>
      <Link
        to={to}
        onClick={onNavigate}
        data-profile-menu-link=""
        className={cn(
          sidebarProfileMenuItemClassName(isDark),
          "flex w-full min-w-0 items-center gap-2"
        )}
        style={isDark ? { color: "#ffffff" } : undefined}
        {...linkHandlers}
      >
        <span
          data-profile-menu-icon
          className={cn(sidebarNavIconShellClass, sidebarProfileMenuIconClassName(isDark))}
          style={isDark ? { color: "#ffffff" } : undefined}
        >
          {renderRouteIconWithAnimRef(icon, iconAnimRef)}
        </span>
        {label}
      </Link>
    </DropdownMenuItem>
  )
}

function ProfileLogOutItem({
  disabled,
  onSignOut,
}: {
  disabled: boolean
  onSignOut: () => void
}) {
  const { iconAnimRef, linkHandlers } = useSidebarNavIconAnimation()

  return (
    <DropdownMenuItem
      variant="destructive"
      disabled={disabled}
      data-profile-logout=""
      className={cn(
        "group/profile-logout-item flex min-h-8 w-full items-center gap-2 rounded-md p-2 text-sm text-[#FE4244] hover:text-[#FE4244] focus:text-[#FE4244] data-[highlighted]:text-[#FE4244]"
      )}
      onClick={onSignOut}
      {...linkHandlers}
    >
      <span
        data-profile-logout-icon
        className={cn(sidebarNavIconShellClass, sidebarLogoutIconClass)}
      >
        {renderRouteIconWithAnimRef(
          <LogoutIcon size={16} className="text-current" aria-hidden />,
          iconAnimRef
        )}
      </span>
      Log out
    </DropdownMenuItem>
  )
}

function ProfileNotificationsItem({
  enabled,
  menuOpen,
  onNavigate,
}: {
  enabled: boolean
  menuOpen: boolean
  onNavigate: () => void
}) {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const { iconAnimRef, linkHandlers } = useSidebarNavIconAnimation()
  const unreadCount = useUnreadNotificationCount(enabled, menuOpen)
  const label = formatNotificationsProfileMenuLabel(unreadCount)
  const hasUnread = unreadCount > 0

  return (
    <DropdownMenuItem asChild>
      <Link
        to={notificationsCentrePath()}
        onClick={onNavigate}
        data-profile-menu-link=""
        className={cn(
          sidebarProfileMenuItemClassName(isDark),
          "flex w-full min-w-0 items-center gap-2",
          hasUnread &&
            (isDark
              ? "!text-yellow-400 hover:!text-yellow-400 focus-visible:!text-yellow-400 data-[highlighted]:!text-yellow-400 hover:[&_svg]:!text-yellow-400 focus-visible:[&_svg]:!text-yellow-400 data-[highlighted]:[&_svg]:!text-yellow-400"
              : "!text-yellow-500 hover:!text-yellow-500 focus-visible:!text-yellow-500 data-[highlighted]:!text-yellow-500 hover:[&_svg]:!text-yellow-500 focus-visible:[&_svg]:!text-yellow-500 data-[highlighted]:[&_svg]:!text-yellow-500")
        )}
        style={
          hasUnread
            ? { color: isDark ? "#facc15" : "#eab308" }
            : isDark
              ? { color: "#ffffff" }
              : undefined
        }
        aria-label={label}
        {...(hasUnread ? { "data-profile-notifications-unread": "" } : {})}
        {...linkHandlers}
      >
        <span
          data-profile-menu-icon
          className={cn(
            sidebarNavIconShellClass,
            hasUnread
              ? cn(
                  "[&_svg]:text-current",
                  isDark
                    ? "!text-yellow-400 group-hover/profile-menu-item:!text-yellow-400 group-focus-visible/profile-menu-item:!text-yellow-400 group-data-[highlighted]/profile-menu-item:!text-yellow-400"
                    : "!text-yellow-500 group-hover/profile-menu-item:!text-yellow-500 group-focus-visible/profile-menu-item:!text-yellow-500 group-data-[highlighted]/profile-menu-item:!text-yellow-500"
                )
              : sidebarProfileMenuIconClassName(isDark)
          )}
          style={
            hasUnread
              ? { color: isDark ? "#facc15" : "#eab308" }
              : isDark
                ? { color: "#ffffff" }
                : undefined
          }
        >
          {renderRouteIconWithAnimRef(
            <BellIcon size={16} className="text-current" aria-hidden />,
            iconAnimRef
          )}
        </span>
        {label}
      </Link>
    </DropdownMenuItem>
  )
}

function ProfileDarkModeItem() {
  const { resolvedTheme, setTheme } = useTheme()
  const { setColorMode } = useColorMode()
  const [mounted, setMounted] = React.useState(false)

  React.useEffect(() => {
    setMounted(true)
  }, [])

  const isDark = resolvedTheme === "dark"

  return (
    <DropdownMenuItem
      data-profile-menu-link=""
      className={cn(sidebarProfileMenuItemClassName(isDark), "justify-between")}
      style={isDark ? { color: "#ffffff" } : undefined}
      onSelect={(event) => event.preventDefault()}
    >
      <span className="flex min-w-0 items-center gap-2">
        <span
          data-profile-menu-icon
          className={cn(sidebarNavIconShellClass, sidebarProfileMenuIconClassName(isDark))}
          style={isDark ? { color: "#ffffff" } : undefined}
        >
          {mounted && isDark ? (
            <MoonIcon size={16} className="text-current" aria-hidden />
          ) : (
            <SunIcon size={16} className="text-current" aria-hidden />
          )}
        </span>
        Dark mode
      </span>
      <Switch
        checked={mounted ? isDark : false}
        disabled={!mounted}
        onCheckedChange={(checked) => {
          const mode = checked ? "dark" : "light"
          setTheme(mode)
          setColorMode(mode)
        }}
        aria-label="Toggle dark mode"
        onClick={(event) => event.stopPropagation()}
      />
    </DropdownMenuItem>
  )
}

export function NavUser({ iconRailOnly = false }: { iconRailOnly?: boolean } = {}) {
  const showMenuChrome = useSidebarExpandedMenuChrome() && !iconRailOnly
  const { isLoaded, user, signOut } = useClerkAuth()
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const location = useLocation()
  const [isSigningOut, setIsSigningOut] = React.useState(false)
  const [profileMenuOpen, setProfileMenuOpen] = React.useState(false)
  const closeMobileSidebar = useCloseMobileSidebar()

  const rememberReturnPath = React.useCallback(() => {
    closeMobileSidebar()
    const currentPath = `${location.pathname}${location.search}`
    if (
      !currentPath.startsWith("/profile") &&
      !currentPath.startsWith("/notifications")
    ) {
      window.sessionStorage.setItem(ACCOUNT_SETTINGS_RETURN_PATH_KEY, currentPath)
      window.sessionStorage.setItem(NOTIFICATIONS_CENTRE_RETURN_PATH_KEY, currentPath)
    }
  }, [closeMobileSidebar, location.pathname, location.search])

  const handleSignOut = React.useCallback(async () => {
    setIsSigningOut(true)
    try {
      await signOut()
      window.location.assign("/auth/signin")
    } catch (e) {
      console.error("Sign out error:", e)
      setIsSigningOut(false)
    }
  }, [signOut])

  const name = userDisplayName(user)
  const email = userEmail(user)
  const avatarSrc = userAvatarSrc(user)
  const initials = userInitials(user)

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size={iconRailOnly ? "default" : "lg"}
              tooltip={showMenuChrome ? undefined : name}
              className={cn(
                navUserTriggerClassName(iconRailOnly),
                showMenuChrome && "overflow-visible"
              )}
              disabled={!isLoaded}
            >
              <Avatar className="h-8 w-8 shrink-0 rounded-[6px] after:rounded-[6px] [&_[data-slot=avatar-image]]:rounded-[6px] [&_[data-slot=avatar-fallback]]:rounded-[6px]">
                <AvatarImage src={isLoaded ? avatarSrc : undefined} alt={name} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              {showMenuChrome ? (
                <>
                  <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                    <span className={cn("truncate font-medium", sidebarTropicalSurfClass)}>
                      {isLoaded ? name : "…"}
                    </span>
                    <span className={cn("truncate text-xs", sidebarSecondaryTextClass)}>
                      {isLoaded ? email : "…"}
                    </span>
                  </div>
                  <ChevronsUpDown
                    className={cn("ml-auto size-4 shrink-0", sidebarGray500Class)}
                    aria-hidden
                  />
                </>
              ) : null}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            data-sidebar-profile-menu
            className={cn(
              "min-w-56 rounded-lg p-0 px-2 py-2",
              // Icon-rail triggers are ~32px; keep a full menu width (CSS also enforces this).
              iconRailOnly
                ? "w-56"
                : "w-(--radix-dropdown-menu-trigger-width)"
            )}
            side={iconRailOnly ? "right" : "top"}
            align="start"
            sideOffset={4}
            avoidCollisions
            collisionPadding={8}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-0 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-[6px] after:rounded-[6px] [&_[data-slot=avatar-image]]:rounded-[6px] [&_[data-slot=avatar-fallback]]:rounded-[6px]">
                  <AvatarImage src={avatarSrc} alt={name} />
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span
                    className="truncate font-medium"
                    style={isDark ? { color: "#ffffff" } : undefined}
                  >
                    {name}
                  </span>
                  <span
                    className={cn("truncate text-xs", !isDark && sidebarSecondaryTextClass)}
                    style={isDark ? { color: "#ffffff" } : undefined}
                  >
                    {email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <ProfileMenuItemLink
                to={accountSettingsTabPath("general")}
                onNavigate={rememberReturnPath}
                label="Account settings"
                icon={<UserIcon size={16} className="text-current" aria-hidden />}
              />
              <ProfileMenuItemLink
                to={accountSettingsTabPath("billing")}
                onNavigate={rememberReturnPath}
                label="Billing"
                icon={
                  <CreditCardIcon size={16} color="inherit" className="text-current" aria-hidden />
                }
              />
              <ProfileNotificationsItem
                enabled={isLoaded}
                menuOpen={profileMenuOpen}
                onNavigate={rememberReturnPath}
              />
              <ProfileDarkModeItem />
            </DropdownMenuGroup>
            <DropdownMenuSeparator className="-mx-2" />
            <ProfileLogOutItem
              disabled={isSigningOut}
              onSignOut={() => {
                void handleSignOut()
              }}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
